import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getLogoPath, getAppName } from '../utils/theme';

const Login: React.FC = () => {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const urlClientSecret = searchParams.get('client_secret');
    if (urlClientSecret) {
      setClientSecret(urlClientSecret);
      // Auto-login with the URL parameter
      handleAutoLogin(urlClientSecret);
    }
  }, [searchParams]);

  const handleAutoLogin = async (secret: string) => {
    setLoading(true);
    setError('');

    try {
      const isValid = await apiService.validateClientSecret(secret);
      if (isValid) {
        setAuth(secret);
        navigate('/ads');
      } else {
        setError('Invalid client secret from URL');
      }
    } catch (err) {
      setError('Failed to authenticate with URL client secret.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isValid = await apiService.validateClientSecret(clientSecret);
      if (isValid) {
        setAuth(clientSecret);
        navigate('/ads');
      } else {
        setError('Invalid client secret');
      }
    } catch (err) {
      setError('Failed to authenticate. Please check your client secret.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img
              src={getLogoPath()}
              alt={getAppName()}
              style={{
                width: '80px',
                height: '80px',
                marginBottom: '16px'
              }}
            />
            <Typography component="h1" variant="h5">
              Client Login
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getAppName()}
            </Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              required
              fullWidth
              id="client-secret"
              label="Client Secret"
              name="clientSecret"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              disabled={loading}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !clientSecret}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;